/**
 * Geração dos relatórios gerenciais, separada da tela.
 *
 * ## Por que é um módulo próprio
 *
 * Os geradores são funções puras — recebem a lista e devolvem linhas —, mas viviam dentro do
 * componente, onde nenhum teste alcança sem montar React. O resultado é que os seis relatórios
 * chegaram a produção somando o campo errado sem nada reclamar: exibiam **MRR** usando a mensalidade
 * líquida do crédito AWS, que é a parcela promocional, não a receita recorrente. O mesmo defeito que o
 * dashboard tinha, com o agravante de o rótulo prometer exatamente o que o número não era.
 *
 * Aqui fora, cada gerador é testável com uma lista literal, e a serialização do CSV também.
 *
 * ## Qual valor os relatórios usam
 *
 * Sempre `mensalidadeSemCreditoUsd`, a recorrência antes do incentivo. Relatório gerencial responde
 * "quanto esta carteira vale por mês", e o crédito é desconto temporário condicionado à aprovação da
 * AWS. A mensalidade faturada continua existindo na API e é o número que o detalhe de cada proposta
 * mostra, onde a pergunta é outra: quanto o cliente paga agora.
 */

import { formatarDataBr, formatarDataIso } from './periodoDashboard.ts';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export interface Financeiro {
  /** Mensalidade faturada, já líquida do crédito. Não usada nos totais gerenciais. */
  readonly mensalidadeUsd: number;
  readonly mensalidadeBrl: number | null;
  /** Recorrência antes do crédito. É o que todo total deste módulo soma. */
  readonly mensalidadeSemCreditoUsd: number;
  readonly consumoUsd: number;
  readonly suporteUsd: number;
  readonly deployUsd: number;
  readonly creditoUsd: number;
  readonly creditoJanelaMeses: number;
  readonly economiaPercentual: number;
  readonly margemPercentual: number;
  readonly planoSuporte: string;
  readonly trilha: string;
  readonly duracaoMeses: number;
}

export interface ItemLista {
  readonly numero: string;
  readonly situacao: string;
  readonly cliente: string;
  readonly projeto: string;
  readonly responsavelEmail: string;
  readonly responsavelSub: string;
  readonly emitidaEm: string;
  readonly validaAte: string;
  readonly versao: number;
  readonly url: string;
  readonly financeiro: Financeiro | null;
  readonly legado: boolean;
}

export type TipoRelatorio =
  'por_periodo' | 'por_vendedor' | 'por_status' | 'receita_projetada' | 'vencendo';

export interface LinhaRelatorio {
  readonly colunas: readonly string[];
}

export interface DadosRelatorio {
  readonly titulo: string;
  readonly cabecalho: readonly string[];
  readonly linhas: readonly LinhaRelatorio[];
  readonly resumo?: string;
}

export interface OpcaoRelatorio {
  readonly id: TipoRelatorio;
  readonly nome: string;
  readonly descricao: string;
  readonly icone: string;
}

export const RELATORIOS: readonly OpcaoRelatorio[] = [
  {
    id: 'por_periodo',
    nome: 'Propostas por Período',
    descricao: 'Contagem e valores agrupados por mês',
    icone: '📅',
  },
  {
    id: 'por_vendedor',
    nome: 'Propostas por Vendedor',
    descricao: 'Ranking de vendedores com totais e médias',
    icone: '👥',
  },
  {
    id: 'por_status',
    nome: 'Propostas por Status',
    descricao: 'Distribuição por situação (vigente, vencida, legado)',
    icone: '📊',
  },
  {
    id: 'receita_projetada',
    nome: 'Receita Projetada (MRR)',
    descricao: 'Receita mensal recorrente por trilha CS/PS',
    icone: '💰',
  },
  {
    id: 'vencendo',
    nome: 'Propostas Vencendo',
    descricao: 'Propostas próximas a expirar (30 dias)',
    icone: '⏰',
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const MESES_NOME = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

/**
 * Nota que acompanha todo total monetário.
 *
 * Existe para o relatório exportado se defender sozinho: a planilha circula fora do painel, e sem a
 * declaração alguém compara o MRR daqui com a fatura do cliente e conclui que um dos dois está errado.
 */
const NOTA_SEM_CREDITO = 'Valores sem desconto do crédito AWS (recorrência)';

export function formatarUsd(v: number): string {
  return 'US$ ' + v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function extrairNome(email: string): string {
  return (
    email
      .split('@')[0]
      ?.split('.')
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' ') ?? email
  );
}

function situacaoLegivel(item: ItemLista): string {
  if (item.legado) return 'Legado';
  if (item.situacao === 'vigente') return 'Vigente';
  if (item.situacao === 'vencida') return 'Vencida';
  return item.situacao.charAt(0).toUpperCase() + item.situacao.slice(1);
}

/**
 * Recorrência mensal de uma proposta, em dólar.
 *
 * Um único ponto de leitura, e não `item.financeiro?.mensalidadeSemCreditoUsd` espalhado por seis
 * geradores: foi a repetição que deixou os seis somando o campo errado de uma vez.
 */
function recorrencia(item: ItemLista): number {
  return item.financeiro?.mensalidadeSemCreditoUsd ?? 0;
}

// ---------------------------------------------------------------------------
// Geradores
// ---------------------------------------------------------------------------

function gerarRelatorioPorPeriodo(itens: readonly ItemLista[]): DadosRelatorio {
  const agrupado = new Map<string, { qtd: number; valorTotal: number }>();

  for (const item of itens) {
    const mesAno = item.emitidaEm.slice(0, 7); // AAAA-MM
    const existente = agrupado.get(mesAno) ?? { qtd: 0, valorTotal: 0 };
    agrupado.set(mesAno, {
      qtd: existente.qtd + 1,
      valorTotal: existente.valorTotal + recorrencia(item),
    });
  }

  const ordenado = [...agrupado.entries()].sort((a, b) => b[0].localeCompare(a[0]));

  const linhas: LinhaRelatorio[] = ordenado.map(([mesAno, dados]) => {
    const [ano, mes] = mesAno.split('-');
    const nomeMes = MESES_NOME[parseInt(mes ?? '1', 10) - 1] ?? mes;
    return {
      colunas: [
        `${nomeMes}/${ano}`,
        String(dados.qtd),
        formatarUsd(dados.valorTotal),
        formatarUsd(dados.qtd > 0 ? dados.valorTotal / dados.qtd : 0),
      ],
    };
  });

  const totalQtd = itens.length;
  const totalValor = itens.reduce((s, i) => s + recorrencia(i), 0);

  return {
    titulo: 'Propostas por Período',
    cabecalho: ['Mês/Ano', 'Quantidade', 'MRR Total', 'Ticket Médio'],
    linhas,
    resumo: `Total: ${String(totalQtd)} propostas | MRR Total: ${formatarUsd(totalValor)} | Ticket Médio: ${formatarUsd(totalQtd > 0 ? totalValor / totalQtd : 0)} | ${NOTA_SEM_CREDITO}`,
  };
}

function gerarRelatorioPorVendedor(itens: readonly ItemLista[]): DadosRelatorio {
  const agrupado = new Map<string, { email: string; qtd: number; valorTotal: number }>();

  for (const item of itens) {
    const email = item.responsavelEmail;
    const existente = agrupado.get(email) ?? { email, qtd: 0, valorTotal: 0 };
    agrupado.set(email, {
      email,
      qtd: existente.qtd + 1,
      valorTotal: existente.valorTotal + recorrencia(item),
    });
  }

  const ordenado = [...agrupado.values()].sort((a, b) => b.valorTotal - a.valorTotal);

  const linhas: LinhaRelatorio[] = ordenado.map((vendedor, idx) => ({
    colunas: [
      String(idx + 1),
      extrairNome(vendedor.email),
      vendedor.email,
      String(vendedor.qtd),
      formatarUsd(vendedor.valorTotal),
      formatarUsd(vendedor.qtd > 0 ? vendedor.valorTotal / vendedor.qtd : 0),
    ],
  }));

  return {
    titulo: 'Propostas por Vendedor',
    cabecalho: ['#', 'Nome', 'Email', 'Propostas', 'MRR Total', 'Ticket Médio'],
    linhas,
    resumo: `${String(ordenado.length)} vendedores no período | ${NOTA_SEM_CREDITO}`,
  };
}

function gerarRelatorioPorStatus(itens: readonly ItemLista[]): DadosRelatorio {
  const agrupado = new Map<string, { qtd: number; valorTotal: number }>();

  for (const item of itens) {
    const status = situacaoLegivel(item);
    const existente = agrupado.get(status) ?? { qtd: 0, valorTotal: 0 };
    agrupado.set(status, {
      qtd: existente.qtd + 1,
      valorTotal: existente.valorTotal + recorrencia(item),
    });
  }

  const ordenado = [...agrupado.entries()].sort((a, b) => b[1].qtd - a[1].qtd);
  const total = itens.length;

  const linhas: LinhaRelatorio[] = ordenado.map(([status, dados]) => ({
    colunas: [
      status,
      String(dados.qtd),
      total > 0 ? `${((dados.qtd / total) * 100).toFixed(1)}%` : '0%',
      formatarUsd(dados.valorTotal),
    ],
  }));

  return {
    titulo: 'Propostas por Status',
    cabecalho: ['Status', 'Quantidade', '% do Total', 'MRR Total'],
    linhas,
    resumo: `Total: ${String(total)} propostas | ${NOTA_SEM_CREDITO}`,
  };
}

function gerarRelatorioReceitaProjetada(itens: readonly ItemLista[]): DadosRelatorio {
  const vigentes = itens.filter((i) => i.situacao === 'vigente' && !i.legado);

  const porTrilha = new Map<string, { qtd: number; mrr: number }>();

  for (const item of vigentes) {
    const trilha = item.financeiro?.trilha ?? 'Indefinida';
    const existente = porTrilha.get(trilha) ?? { qtd: 0, mrr: 0 };
    porTrilha.set(trilha, { qtd: existente.qtd + 1, mrr: existente.mrr + recorrencia(item) });
  }

  const ordenado = [...porTrilha.entries()].sort((a, b) => b[1].mrr - a[1].mrr);

  const linhas: LinhaRelatorio[] = ordenado.map(([trilha, dados]) => ({
    colunas: [trilha, String(dados.qtd), formatarUsd(dados.mrr), formatarUsd(dados.mrr * 12)],
  }));

  const mrrTotal = vigentes.reduce((s, i) => s + recorrencia(i), 0);

  return {
    titulo: 'Receita Projetada (MRR)',
    cabecalho: ['Trilha', 'Contratos Vigentes', 'MRR (mensal)', 'ARR (anual)'],
    linhas,
    resumo: `MRR Total: ${formatarUsd(mrrTotal)} | ARR Total: ${formatarUsd(mrrTotal * 12)} | Apenas propostas vigentes (não legado) | ${NOTA_SEM_CREDITO}`,
  };
}

function gerarRelatorioVencendo(itens: readonly ItemLista[], hojeIso?: string): DadosRelatorio {
  /*
   * `hojeIso` é injetável para o teste não depender da data da máquina. Em produção ninguém passa, e o
   * comportamento é o mesmo de antes.
   */
  const hoje = hojeIso ?? formatarDataIso(new Date());
  const em30dias = new Date(`${hoje}T00:00:00Z`);
  em30dias.setUTCDate(em30dias.getUTCDate() + 30);
  const limite = em30dias.toISOString().slice(0, 10);

  const vencendo = itens.filter(
    (i) => i.situacao === 'vigente' && !i.legado && i.validaAte >= hoje && i.validaAte <= limite,
  );

  const ordenado = [...vencendo].sort((a, b) => a.validaAte.localeCompare(b.validaAte));

  const linhas: LinhaRelatorio[] = ordenado.map((item) => {
    const diasRestantes = Math.ceil(
      (new Date(item.validaAte + 'T00:00:00').getTime() - new Date(hoje + 'T00:00:00').getTime()) /
        86400000,
    );
    return {
      colunas: [
        item.numero,
        item.cliente,
        extrairNome(item.responsavelEmail),
        formatarDataBr(item.validaAte),
        `${String(diasRestantes)} dias`,
        formatarUsd(recorrencia(item)),
      ],
    };
  });

  const mrrEmRisco = vencendo.reduce((s, i) => s + recorrencia(i), 0);

  return {
    titulo: 'Propostas Vencendo (próximos 30 dias)',
    cabecalho: ['Nº', 'Cliente', 'Vendedor', 'Vence em', 'Dias Restantes', 'MRR'],
    linhas,
    resumo: `${String(vencendo.length)} propostas vencendo | MRR em risco: ${formatarUsd(mrrEmRisco)} | ${NOTA_SEM_CREDITO}`,
  };
}

export function gerarRelatorio(
  tipo: TipoRelatorio,
  itens: readonly ItemLista[],
  hojeIso?: string,
): DadosRelatorio {
  switch (tipo) {
    case 'por_periodo':
      return gerarRelatorioPorPeriodo(itens);
    case 'por_vendedor':
      return gerarRelatorioPorVendedor(itens);
    case 'por_status':
      return gerarRelatorioPorStatus(itens);
    case 'receita_projetada':
      return gerarRelatorioReceitaProjetada(itens);
    case 'vencendo':
      return gerarRelatorioVencendo(itens, hojeIso);
  }
}

// ---------------------------------------------------------------------------
// Serialização do CSV
// ---------------------------------------------------------------------------

export function escaparCsv(valor: string): string {
  if (valor.includes(',') || valor.includes('"') || valor.includes('\n')) {
    return `"${valor.replace(/"/g, '""')}"`;
  }
  return valor;
}

/**
 * Monta o conteúdo do CSV. Sem `Blob`, sem `document`: só texto.
 *
 * Separado do download de propósito. A serialização é onde mora o risco real — escape de vírgula em
 * nome de cliente, resumo com aspas — e é exatamente a parte que não se consegue testar quando está
 * grudada no `document.createElement`.
 */
export function montarCsv(dados: DadosRelatorio, descricaoPeriodo: string, geradoEm: Date): string {
  const linhasCsv: string[] = [];

  linhasCsv.push(escaparCsv(dados.titulo));
  linhasCsv.push(escaparCsv(`Período: ${descricaoPeriodo}`));
  linhasCsv.push(escaparCsv(`Gerado em: ${geradoEm.toLocaleString('pt-BR')}`));
  linhasCsv.push('');

  linhasCsv.push(dados.cabecalho.map(escaparCsv).join(','));

  for (const linha of dados.linhas) {
    linhasCsv.push(linha.colunas.map(escaparCsv).join(','));
  }

  if (dados.resumo !== undefined) {
    linhasCsv.push('');
    linhasCsv.push(escaparCsv(dados.resumo));
  }

  return linhasCsv.join('\r\n');
}

/** Nome do arquivo exportado, derivado do título. */
export function nomeArquivo(dados: DadosRelatorio, extensao: string, hojeIso: string): string {
  const base = dados.titulo
    .replace(/[^a-zA-Z0-9áéíóúâêîôûãõçÁÉÍÓÚÂÊÎÔÛÃÕÇ ]/g, '')
    .replace(/\s+/g, '_');

  return `${base}_${hojeIso}.${extensao}`;
}
